import React, { useEffect, useState, useRef } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import BookingModal from './BookingModal';
import Login from './Login';
import CustomDateHeader from './CustomDateHeader';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from './config';
import { useSwipeable } from 'react-swipeable';
import EventDetailsModal from './EventDetailsModal';
import HomePage from './HomePage';
import EventPage from './EventPage';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${hash % 360}, 70%, 70%)`;
}

function locationColor(name) {
  if (!name) return '#f7dc6f';
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const hue = ((hash % 360) + 360) % 360;
  return `hsl(${hue}, 55%, 75%)`;
}

function locationColorDark(name) {
  if (!name) return '#7d6608';
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const hue = ((hash % 360) + 360) % 360;
  return `hsl(${hue}, 60%, 35%)`;
}

function isTokenExpired(token) {
  try {
    const { exp } = jwtDecode(token);
    return !exp || exp < Date.now() / 1000;
  } catch {
    return true;
  }
}

async function authFetch(url, options = {}) {
  const token = localStorage.getItem('access');
  if (!token || isTokenExpired(token)) {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    window.location.reload();
    return new Response(null, { status: 401 });
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (response.status === 401) {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    window.location.reload();
  }

  return response;
}

function App() {

  const calendarRef = useRef();
  const [djs, setDjs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(new Set());
  const [dayNameMap, setDayNameMap] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [focusedDate, setFocusedDate] = useState(new Date());
  const [currentView, setCurrentView] = useState(Views.MONTH);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('access'));
  const [addDJModalOpen, setAddDJModalOpen] = useState(false);
  const [djForm, setDJForm] = useState({
    name: '',
    instagram: '',
    phone: '',
    email: '',
    music_style: '',
    location_ids: []
  });

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [posterUrl, setPosterUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState('home'); // 'home' or 'calendar' or 'event'
  const [currentEventDate, setCurrentEventDate] = useState(null);

  // Location state
  const [locations, setLocations] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]); // filter chips (viewing)
  const [activeLocationId, setActiveLocationId] = useState(null);     // dropdown (creating)

  // Create Event modal state
  const [createEventModalOpen, setCreateEventModalOpen] = useState(false);
  const [createEventDate, setCreateEventDate] = useState(null);
  const [createEventForm, setCreateEventForm] = useState({ event_name: '', location_id: null });

  // URL routing logic
  useEffect(() => {
    const path = window.location.pathname;
    const eventMatch = path.match(/^\/event\/(\d{4}-\d{2}-\d{2})$/);

    if (eventMatch) {
      setCurrentPage('event');
      setCurrentEventDate(eventMatch[1]);
    } else if (path === '/calendar') {
      setCurrentPage('calendar');
    } else {
      setCurrentPage('home');
    }

    // Handle browser back/forward buttons
    const handlePopState = () => {
      const newPath = window.location.pathname;
      const newEventMatch = newPath.match(/^\/event\/(\d{4}-\d{2}-\d{2})$/);

      if (newEventMatch) {
        setCurrentPage('event');
        setCurrentEventDate(newEventMatch[1]);
      } else if (newPath === '/calendar') {
        setCurrentPage('calendar');
      } else {
        setCurrentPage('home');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Navigation helpers that update URL
  const navigateToHome = () => {
    window.history.pushState(null, '', '/');
    setCurrentPage('home');
    setCurrentEventDate(null);
  };

  const navigateToCalendar = () => {
    window.history.pushState(null, '', '/calendar');
    setCurrentPage('calendar');
    setCurrentEventDate(null);
  };

  const navigateToEvent = (eventDate) => {
    window.history.pushState(null, '', `/event/${eventDate}`);
    setCurrentPage('event');
    setCurrentEventDate(eventDate);
  };

  const navigateToLogin = () => {
    setCurrentPage('login');
  };

  // Fetch locations on login
  const fetchLocations = async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/api/locations/`);
      const data = await res.json();
      setLocations(data);
      if (data.length > 0) {
        setSelectedLocationIds(data.map(l => l.id));
        setActiveLocationId(data[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }
  };

  // Function declarations (moved up to avoid hoisting issues)
  const fetchBookingsForMonth = async (year, month, locationIds) => {
    const locIds = locationIds || selectedLocationIds;
    const locParam = locIds.length > 0 ? `?location_id=${locIds.join(',')}` : '';
    const key = `${year}-${month}-${locIds.join(',')}`;
    if (loaded.has(key)) return;

    const response = await authFetch(`${API_BASE_URL}/api/bookings/month/${year}/${month}/${locParam}`);
    const data = await response.json();

    const newDayNames = {};
    const calendarEvents = Array.isArray(data)
      ? data.flatMap(item => {
        const dateKey = item.date;
        if (item.day_name) {
          if (!newDayNames[dateKey]) newDayNames[dateKey] = [];
          newDayNames[dateKey].push({
            name: item.day_name,
            locationName: item.location?.name || '',
            locationId: item.location?.id || null,
            scheduleId: item.id,
          });
        }
        return item.slots.map(slot => ({
          id: slot.id,
          title: `${slot.dj} (${slot.start_time} - ${slot.end_time})`,
          start: new Date(item.date + 'T' + slot.start_time),
          end: new Date(item.date + 'T' + slot.end_time),
          allDay: false,
          resource: {
            color: slot.dj ? stringToColor(slot.dj) : '#ccc',
            locationName: item.location?.name || '',
            locationId: item.location?.id || null,
            scheduleId: item.id,
          }
        }));
      })
      : [];

    setDayNameMap(prev => ({ ...prev, ...newDayNames }));
    setEvents(prev => [...prev, ...calendarEvents]);
    setLoaded(prev => new Set(prev).add(key));
  };

  const fetchDJs = async () => {
    const res = await authFetch(`${API_BASE_URL}/api/djs/`);
    const data = await res.json();
    setDjs(data);
  };

  // Initial login fetch of data.
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchLocations();
    fetchDJs();
  }, [isLoggedIn]);

  // Fetch bookings once locations are loaded
  useEffect(() => {
    if (!isLoggedIn || locations.length === 0) return;
    const now = new Date();
    fetchBookingsForMonth(now.getFullYear(), now.getMonth() + 1, selectedLocationIds);
  }, [isLoggedIn, locations]);

  // Re-fetch when location filter changes
  useEffect(() => {
    if (!isLoggedIn || locations.length === 0) return;
    // Clear current data and re-fetch
    setEvents([]);
    setLoaded(new Set());
    setDayNameMap({});
    const now = focusedDate || new Date();
    fetchBookingsForMonth(now.getFullYear(), now.getMonth() + 1, selectedLocationIds);
  }, [selectedLocationIds]);


  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && selectedEvent) {
        (async () => {
          if (!selectedEvent.id) {
            alert("This event cannot be deleted (missing ID).");
            return;
          }

          const confirmed = window.confirm(`Delete "${selectedEvent.title}"?`);
          if (!confirmed) return;

          try {
            // Delete the slot on the backend
            await authFetch(`${API_BASE_URL}/api/schedule/${selectedEvent.id}/`, {
              method: 'DELETE'
            });

            setEvents((prevEvents) => {
              const updated = prevEvents.filter(ev => ev !== selectedEvent);
              const dateKey = selectedEvent.start.toISOString().split('T')[0];

              const remainingSlots = updated.filter(ev =>
                !ev.resource?.type && ev.start.toISOString().startsWith(dateKey)
              );

              // If no more slots remain, remove label + cleanup
              if (remainingSlots.length === 0) {
                const cleaned = updated.filter(ev =>
                  !(ev.resource?.type === 'label' && ev.start.toISOString().startsWith(dateKey))
                );

                setDayNameMap(prev => {
                  const next = { ...prev };
                  delete next[dateKey];
                  return next;
                });

                const locParam = activeLocationId ? `?location_id=${activeLocationId}` : '';
                authFetch(`${API_BASE_URL}/api/schedule/cleanup/${dateKey}/${locParam}`, {
                  method: 'DELETE'
                });

                return cleaned;
              }

              return updated;
            });

            setSelectedEvent(null);
          } catch (err) {
            alert("Failed to delete event.");
          }
        })();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEvent]);


  // Show home page (public access)
  if (currentPage === 'home') {
    return (
      <HomePage
        onNavigateToCalendar={navigateToCalendar}
        onNavigateToLogin={navigateToLogin}
        onNavigateToEvent={navigateToEvent}
        isLoggedIn={isLoggedIn}
      />
    );
  }

  // Show individual event page (public access)
  if (currentPage === 'event') {
    return (
      <EventPage
        eventDate={currentEventDate}
        onNavigateBack={navigateToHome}
        onNavigateToLogin={navigateToLogin}
        isLoggedIn={isLoggedIn}
      />
    );
  }

  // Show login page when requested
  if (currentPage === 'login') {
    return <Login onLogin={() => {
      setIsLoggedIn(true);
      navigateToHome();
    }} />;
  }

  // Show logins screen if we are not logged and trying to access calendar
  if (!isLoggedIn) return <Login onLogin={() => setIsLoggedIn(true)} />;

  const handleNavigate = (date) => {
    setFocusedDate(date);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    fetchBookingsForMonth(year, month);
  };

  const handleAddDayLabel = (dateKey) => {
    setCreateEventDate(dateKey);
    setCreateEventForm({ event_name: '', location_id: activeLocationId });
    setCreateEventModalOpen(true);
  };

  const handleCreateEventSubmit = async () => {
    const { event_name, location_id } = createEventForm;
    if (!event_name.trim()) return alert("Event name is required");
    if (!location_id) return alert("Please select a location");

    const response = await authFetch(`${API_BASE_URL}/api/schedule/day-label/`, {
      method: "POST",
      body: JSON.stringify({ date: createEventDate, event_name, location_id }),
    });

    if (response.ok) {
      const result = await response.json();
      const schedule = result.schedule;
      setDayNameMap(prev => {
        const existing = prev[createEventDate] || [];
        return {
          ...prev,
          [createEventDate]: [...existing, {
            name: event_name,
            locationName: schedule?.location?.name || '',
            locationId: schedule?.location?.id || location_id,
            scheduleId: schedule?.id,
          }]
        };
      });
      setCreateEventModalOpen(false);
    } else {
      alert("Failed to save event");
    }
  };

  const handleRemoveDayLabel = async (dateKey, scheduleId) => {
    const confirmed = window.confirm(`Remove event label for ${dateKey}?`);
    if (!confirmed) return;

    try {
      const locParam = activeLocationId ? `?location_id=${activeLocationId}` : '';
      const response = await authFetch(`${API_BASE_URL}/api/schedule/cleanup/${dateKey}/${locParam}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Remove label from dayNameMap
        setDayNameMap(prev => {
          const next = { ...prev };
          if (scheduleId && Array.isArray(next[dateKey])) {
            next[dateKey] = next[dateKey].filter(e => e.scheduleId !== scheduleId);
            if (next[dateKey].length === 0) delete next[dateKey];
          } else {
            delete next[dateKey];
          }
          return next;
        });

        // Remove all label events on this date
        setEvents(prev =>
          prev.filter(ev =>
            !(ev.resource?.type === 'label' && ev.start.toISOString().startsWith(dateKey))
          )
        );
        setModalOpen(false);
      } else {

        alert("That's not nice. There is a DJ playing that day. Remove the DJs first, then delete the event. To do this, just click on the event and press delete.");
        setModalOpen(false);
      }
    } catch (err) {
      console.error(err);
      setModalOpen(false);
      alert("Something went wrong somewhere, text Gjorgji.");
    }
  };

  const handleSelectSlot = async ({ start, end }) => {
    const dateStr = format(start, 'yyyy-MM-dd');

    if (currentView === 'month') {

      // Only trigger modal if there is already a label for this day
      if (dayNameMap[dateStr]) {
        setSelectedDate(format(start, 'yyyy-MM-dd'));
        setModalOpen(true);
      } else {
        // Do not open modal; maybe give feedback or just ignore
        console.log("No event label for this day yet.");
      }
    } else {
      const startTime = format(start, 'HH:mm');
      const endTime = format(end, 'HH:mm');
      const dateStr = format(start, 'yyyy-MM-dd');

      if (djs.length === 0) await fetchDJs();
      const nameList = djs.map((dj, i) => `${i + 1}: ${dj.name}`).join('\n');
      const input = prompt(
        `Booking slot for ${dateStr} from ${startTime} to ${endTime}\n\nSelect a DJ:\n${nameList}`
      );
      const index = parseInt(input) - 1;
      if (isNaN(index) || !djs[index]) {
        alert("Invalid selection");
        return;
      }

      const selectedDJ = djs[index];
      const payload = { date: dateStr, dj_id: selectedDJ.id, start_time: startTime, end_time: endTime, location_id: activeLocationId };

      const response = await authFetch(`${API_BASE_URL}/api/schedule/`, {
        method: "POST",
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (response.ok) {
        setEvents(prev => [...prev, {
          id: result.id,
          title: `${result.dj} (${result.start_time} - ${result.end_time})`,
          start: new Date(dateStr + 'T' + result.start_time),
          end: new Date(dateStr + 'T' + result.end_time),
          allDay: false,
          resource: { color: stringToColor(result.dj) }
        }]);
      } else {
        alert("Booking failed: " + result.error);
      }
    }
  };

  const handleSubmitDJ = async () => {
    if (!djForm.name.trim()) return alert("Name is required");

    const response = await authFetch(`${API_BASE_URL}/api/create_dj/`, {
      method: "POST",
      body: JSON.stringify(djForm)
    });

    if (response.ok) {
      const created = await response.json();
      setDjs(prev => [...prev, created]);
      setDJForm({ name: '', instagram: '', phone: '', email: '', music_style: '', location_ids: [] });
      setAddDJModalOpen(false);
    } else {
      alert("Failed to add DJ");
    }
  };
  const handleModalConfirm = async ({ dj_id, start_time, end_time }) => {
    const payload = { date: selectedDate, dj_id, start_time, end_time, location_id: activeLocationId };

    const response = await authFetch(`${API_BASE_URL}/api/schedule/`, {
      method: "POST",
      body: JSON.stringify(payload)
    });

    const result = await response.json();
    if (response.ok) {
      // Update dayNameMap with array format
      setDayNameMap(prev => {
        const existing = prev[selectedDate] || [];
        if (existing.length === 0) {
          return {
            ...prev,
            [selectedDate]: [{
              name: result.event_name,
              locationName: '',
              locationId: activeLocationId,
              scheduleId: null,
            }]
          };
        }
        return prev;
      });

      const labelStart = new Date(selectedDate) - 2;

      const slotEvent = {
        id: result.slot_id,
        title: `${result.dj} (${result.start_time} - ${result.end_time})`,
        start: new Date(selectedDate + 'T' + result.start_time),
        end: new Date(selectedDate + 'T' + result.end_time),
        allDay: false,
        resource: { color: stringToColor(result.dj) }
      };

      setEvents(prev => [
        ...prev.filter(e => !(e.resource?.type === 'label' && e.start.toDateString() === labelStart.toDateString())),
        slotEvent
      ]);

      setModalOpen(false);
    } else {
      alert("Booking failed: " + result.error);
    }
  };

  const eventStyleGetter = (event) => {
    const isSelected = selectedEvent && event.title === selectedEvent.title &&
      event.start.getTime() === selectedEvent.start.getTime();

    return {
      style: {
        backgroundColor: isSelected ? '#d4fddc' : event.resource?.color || '#999',
        color: '#000',
        border: isSelected ? '2px solid green' : '1px solid #ccc',
        borderRadius: '5px',
        padding: '2px'
      }
    };
  };

  const handleLogout = () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    window.location.reload();
  };

  const handleDelete = async (event) => {
    if (!event.id) return;

    const confirmed = window.confirm(`Delete "${event.title}"?`);
    if (!confirmed) return;

    try {
      await authFetch(`${API_BASE_URL}/api/schedule/${event.id}/`, { method: 'DELETE' });
      setEvents(prev => prev.filter(e => e.id !== event.id));
    } catch (err) {
      alert("Failed to delete.");
    }
  };

  const toggleLocationFilter = (locationId) => {
    // Radio-style: select only this one location
    setSelectedLocationIds([locationId]);
    setActiveLocationId(locationId);
  };

  const selectAllLocations = () => {
    setSelectedLocationIds(locations.map(l => l.id));
  };

  // When multiple locations are selected ("All"), hide DJ slot events — only show day labels in headers
  const isAllLocationsSelected = selectedLocationIds.length > 1;
  const visibleEvents = isAllLocationsSelected ? [] : events;


  function SwipeableEvent({ event }) {
    const handlers = useSwipeable({
      onSwipedLeft: () => handleDelete(event),
      onSwipedRight: () => handleDelete(event),
      trackMouse: true
    });

    const locName = event.resource?.locationName;

    return (
      <div {...handlers} style={{ padding: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
        {locName && (
          <span
            title={locName}
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: locationColorDark(locName),
              border: `1px solid ${locationColorDark(locName)}`,
              flexShrink: 0,
            }}
          />
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {event.title}
        </span>
      </div>
    );
  }


  return (

    <div>
      <div style={{
        textAlign: 'center',
        position: 'relative',
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img
            src="/static/img/monk.svg"
            alt="Djedjuler"
            style={{ height: '80px', maxWidth: '100%', margin: '0 auto', display: 'block' }}
          />
          <div style={{
            position: 'absolute',
            bottom: '10px',
            left: '85%',
            transform: 'translateX(-50%) rotate(-20deg)',
            backgroundColor: 'red',
            color: 'white',
            fontWeight: 'bold',
            padding: '2px 8px',
            fontSize: '12px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
            whiteSpace: 'nowrap',
          }}>
            djedjuler
          </div>
        </div>
      </div>
      <button
        onClick={handleLogout}
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          background: 'transparent',
          border: 'none',
          color: '#333',
          fontSize: '14px',
          cursor: 'pointer',
          textDecoration: 'underline'
        }}
      >
        Logout
      </button>
      <button
        onClick={navigateToHome}
        style={{
          position: 'absolute',
          top: 20,
          left: 20,
          background: 'transparent',
          border: 'none',
          color: '#333',
          fontSize: '14px',
          cursor: 'pointer',
          textDecoration: 'underline',
          fontWeight: '600'
        }}
      >
        🏠 Home Feed
      </button>
      <button onClick={() => setAddDJModalOpen(true)}
        style={{
          position: 'absolute',
          top: 50,
          right: 20,
          background: 'transparent',
          border: 'none',
          color: '#333',
          fontSize: '14px',
          cursor: 'pointer',
          textDecoration: 'underline',
          fontWeight: '600'
        }}>➕ Add DJ</button>

      {/* Location filter bar */}
      {locations.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 16px',
          borderBottom: '1px solid #eee',
          marginBottom: '8px',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={selectAllLocations}
            style={{
              padding: '4px 12px',
              borderRadius: '14px',
              border: '1px solid #ccc',
              fontSize: '12px',
              cursor: 'pointer',
              backgroundColor: isAllLocationsSelected ? '#333' : '#fff',
              color: isAllLocationsSelected ? '#fff' : '#333',
              fontWeight: 600,
            }}
          >All</button>
          {locations.map(loc => {
            const active = selectedLocationIds.length === 1 && selectedLocationIds[0] === loc.id;
            const bg = locationColor(loc.name);
            const fg = locationColorDark(loc.name);
            return (
              <button
                key={loc.id}
                onClick={() => toggleLocationFilter(loc.id)}
                style={{
                  padding: '4px 12px',
                  borderRadius: '14px',
                  border: `2px solid ${fg}`,
                  fontSize: '12px',
                  cursor: 'pointer',
                  backgroundColor: active ? bg : '#fff',
                  color: active ? fg : '#999',
                  fontWeight: 600,
                  opacity: active ? 1 : 0.5,
                }}
              >{loc.name}</button>
            );
          })}
        </div>
      )}

      {addDJModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999
        }}>
          <div style={{
            background: '#fff',
            color: '#111',
            padding: '2rem',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{ marginTop: 0, fontSize: '1.5rem' }}>Add DJ</h2>

            {[
              ['Name', 'name'],
              ['Instagram', 'instagram'],
              ['Phone', 'phone'],
              ['Email', 'email'],
              ['Music style', 'music_style']
            ].map(([label, field]) => (
              <div key={field} style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.3rem' }}>
                  {label}
                </label>
                <input
                  type="text"
                  value={djForm[field]}
                  onChange={(e) => setDJForm({ ...djForm, [field]: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            ))}

            {/* Location checkboxes */}
            {locations.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Locations
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {locations.map(loc => (
                    <label key={loc.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                      <input
                        type="checkbox"
                        checked={djForm.location_ids.includes(loc.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setDJForm({ ...djForm, location_ids: [...djForm.location_ids, loc.id] });
                          } else {
                            setDJForm({ ...djForm, location_ids: djForm.location_ids.filter(id => id !== loc.id) });
                          }
                        }}
                      />
                      {loc.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={handleSubmitDJ}
                style={{
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}>
                Add
              </button>
              <button
                onClick={() => setAddDJModalOpen(false)}
                style={{
                  backgroundColor: '#eee',
                  color: '#111',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {createEventModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999
        }}>
          <div style={{
            background: '#fff',
            color: '#111',
            padding: '2rem',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{ marginTop: 0, fontSize: '1.5rem' }}>Create Event</h2>
            <p style={{ color: '#666', margin: '0 0 1.2rem 0', fontSize: '14px' }}>
              {createEventDate}
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.3rem' }}>
                Event Name
              </label>
              <input
                type="text"
                value={createEventForm.event_name}
                onChange={(e) => setCreateEventForm({ ...createEventForm, event_name: e.target.value })}
                placeholder="e.g. Summer BBQ Session"
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '1.2rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.3rem' }}>
                Location
              </label>
              <select
                value={createEventForm.location_id || ''}
                onChange={(e) => setCreateEventForm({ ...createEventForm, location_id: parseInt(e.target.value) })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">-- Select Location --</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={handleCreateEventSubmit}
                style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1.2rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}>
                Create
              </button>
              <button
                onClick={() => setCreateEventModalOpen(false)}
                style={{
                  backgroundColor: '#eee',
                  color: '#111',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Calendar
        ref={calendarRef}
        selectable
        localizer={localizer}
        events={visibleEvents}
        startAccessor="start"
        endAccessor="end"
        defaultView={Views.MONTH}
        views={['month', 'week', 'day']}
        step={30}
        timeslots={2}
        min={new Date(2023, 0, 1, 10, 0)}
        max={new Date(2023, 0, 1, 23, 59)}
        scrollToTime={new Date(2023, 0, 1, 10, 0)}
        onSelectSlot={handleSelectSlot}
        eventPropGetter={eventStyleGetter}
        onContextMenu={(e) => e.preventDefault()}
        onSelectEvent={async (event) => {
          setSelectedEvent(event);
          setDetailsModalOpen(true);

          const dateStr = event.start.toISOString().split('T')[0];
          try {
            const locParam = event.resource?.locationId ? `?location_id=${event.resource.locationId}` : '';
            const res = await authFetch(`${API_BASE_URL}/api/schedule/${dateStr}/poster/${locParam}`);
            if (res.ok) {
              const data = await res.json();
              setPosterUrl(`${API_BASE_URL}${data.poster_url}`); // this should be the full URL path
            } else {
              setPosterUrl(null);
            }
          } catch {
            setPosterUrl(null);
          }
        }}
        onNavigate={handleNavigate}
        onView={(view) => setCurrentView(view)}
        popup
        eventLayout="no-overlap"
        components={{
          event: SwipeableEvent,
          month: {
            dateHeader: (props) => <CustomDateHeader {...props} dayNameMap={dayNameMap} onAddDayLabel={handleAddDayLabel} onRemoveLabel={handleRemoveDayLabel}
            />
          }
        }}
        style={{ height: '90vh' }}
      />
      <BookingModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
        djs={djs}
        date={selectedDate}
        locationId={activeLocationId}
      />
      <EventDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        event={selectedEvent}
        posterUrl={posterUrl}
        djs={djs}
        onUpdate={(updatedEvent) => {
          // Replace the event in state
          setEvents(prev =>
            prev.map(e =>
              e.id === updatedEvent.id
                ? {
                  ...e,
                  title: `${updatedEvent.dj} (${updatedEvent.start_time} - ${updatedEvent.end_time})`,
                  start: new Date(updatedEvent.date + 'T' + updatedEvent.start_time),
                  end: new Date(updatedEvent.date + 'T' + updatedEvent.end_time),
                  resource: { color: stringToColor(updatedEvent.dj) }
                }
                : e
            )
          );
          setSelectedEvent(null);
          setDetailsModalOpen(false);
        }}
      />
    </div>
  );
}

export default App;
